import React, { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import axios from "axios";
import { useNavigate, NavLink } from "react-router-dom";
import { useDispatch } from "react-redux";
import { showToast } from "../../hooks/showToast";
import { axiosInstance } from "../../utils/commonInstance";
import { checkObjectForNullUndefinedOrEmpty, convertToBase64 } from "../../utils/commonFunction";
import { BackButtonModal } from "../../components/BackButtonModal";

const FILE_SIZE = 5 * 1024 * 1024;
const SUPPORTED_FORMATS = ["application/pdf"];

const schema = yup.object().shape({
  vendor_name: yup
    .string()
    .required("Please Enter Name")
    .matches(/^[A-Za-z\s]+$/, "Please enter name in alphabet only")
    .max(150, "Length should be less than 150 characters")
    .test("no-spaces-at-ends", "Please Enter Valid Input", (value) => {
      if (!value) return true; // Skip empty values (use required() to handle this)
      return value.trim() === value;
    }),
  email: yup
    .string()
    .required("Please enter Email")
    .matches(/^[\w-\.]+@([\w-]+\.)+[\w-]{2,4}$/, 'Email Id should be in "xyz@gmail.com" format')
    .max(50, "Length of the Email id should be less than 50 characters")
    .email('Email Id should be in "xyz@gmail.com" format')
    .test("no-spaces-at-ends", "Please Enter Valid Input", (value) => {
      if (!value) return true; // Skip empty values (use required() to handle this)
      return value.trim() === value;
    }),

  mobile_no: yup
    .string()
    .required("Please enter Mobile No.")
    .matches(/^[6-9][0-9]{9}$/, "Mobile No. must be numeric and valid"),

  gst_no: yup
    .string()
    .required("Please enter GST No.")
    .matches(/^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z][0-9]Z[a-zA-Z0-9]$/, "Please enter correct GST No.")
    .test("no-spaces", "Please enter correct GST No.", (value) => !/\s/.test(value)),

  tan_no: yup
    .string()
    .required("Please Enter TAN No.")
    .matches(/^[A-Z]{4}[0-9]{5}[A-Z]$/, "Please enter the correct TAN No."),

  pan_no: yup
    .string()
    .required("Please enter PAN No.")
    .matches(/^[A-Z]{5}[0-9]{4}[A-Z]$/, "Please enter correct PAN No."),

  //   epfo_no: yup
  //     .string()
  //     .required("Please enter EPFO No.")
  //     .matches(/^[A-Z]{5}[0-9]{10,17}$/, "Please enter correct EPFO No."),
  epfo_no: yup
    .string()
    .required("Please enter EPFO No.")
    .matches(/^[A-Z]{5}/, "Please enter correct EPFO No.")
    .matches(/[0-9]{10,17}$/, "Please Enter EPFO No. Between 15 to 22 Characters")
    .min(15, "EPFO No. must be at least 15 characters long")
    .max(22, "EPFO No. cannot exceed 22 characters"),

  security_certificate_no: yup.string().required("Please enter Security Certificate No."),

  agreement_file: yup
    .mixed()
    .test("required", "Please upload AGREEMENT file in PDF format", (value) => {
      return value && value.length > 0;
    })
    .test("fileSize", "Please upload PDF file only & less than 5mb", (value) => {
      return value && value[0] ? value[0]?.size <= FILE_SIZE : true;
    })
    .test("fileType", "Please upload PDF file only & less than 5mb", (value) => {
      return value && value[0] ? SUPPORTED_FORMATS.includes(value[0].type) : true;
    }),
});

const VendorForm = ({ toggleFormFillrter, manualFetchData, isView, isEdit, previousData }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [modal, setModal] = useState({
    isOpen: false,
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleModalopen = () => {
    setModal((prevData) => ({
      ...prevData,
      isOpen: true,
    }));
  };

  const handleClose = () => {
    setModal((prevData) => ({
      ...prevData,
      isOpen: false,
    }));
  };

  const {
    register,
    handleSubmit,
    setValue,
    reset,
    watch,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(schema),
    mode: "all",
    defaultValues: {
      is_active: true,
    },
  });

  const [agreementFileName, setAgreementFileName] = useState("");
  useEffect(() => {
    if (isView || isEdit) {
      setValue("vendor_name", previousData?.vendor_name);
      setValue("email", previousData?.email);
      setValue("mobile_no", previousData?.mobile_no);
      setValue("gst_no", previousData?.gst_no);
      setValue("tan_no", previousData?.tan_no);
      setValue("pan_no", previousData?.pan_no);
      setValue("epfo_no", previousData?.epfo_no);
      setValue("security_certificate_no", previousData?.security_certificate_no);
      setValue("is_active", previousData?.is_active);

      if (previousData?.agreement_file_url) {
        fetch(previousData?.agreement_file_url, { mode: "no-cors" })
          .then((res) => res.blob())
          .then((blob) => {
            const file = new File([blob], "agreement_file.pdf", {
              type: "application/pdf",
            });

            setValue("agreement_file", [file]);
          });
      }

      // setValue("agreement_file", previousData?.pan_no);
    }
  }, [isEdit, isView, previousData, setValue]);
  const onSubmit = async (data) => {
    setIsSubmitting(true);
    try {
      // const response = await axios.post(process.env.REACT_APP_BACKEND_API_URL + "login", {...data,'mode_of_login':'Web'})

      const dynamicEndPoint = isEdit ? `web/editvendor/${previousData?.id}` : "web/addvendor";

      data.agreement_file = await convertToBase64(data?.agreement_file && data?.agreement_file[0]);
      const response = await axiosInstance.post(dynamicEndPoint, {
        ...data,
      });

      if (response?.data?.statusCode !== 200) {
        showToast(response?.data?.message, "error");
        setIsSubmitting(false);
        return false;
      }

      await showToast(response?.data?.message, "success");
      await manualFetchData();
      await toggleFormFillrter();
    } catch (error) {
      showToast(error?.data?.message, "error");
      setIsSubmitting(false);
    }
  };

  let result = checkObjectForNullUndefinedOrEmpty(watch());

  // useEffect(()=>{
  //   if(isView || isEdit){
  //     setValue('location_name', previousData?.location_name)
  //     setValue('location_is_active', previousData?.location_is_active)
  //   }

  // },[isEdit, isView, previousData, setValue])

  return (
    <>
      <section class="userlist-newuser content mobile">
        <div class="d-flex justify-content-end align-items-center mb-4 gap-3 flex-wrap">
          <h3 class="mb-0">
            <strong>{isEdit ? "Update" : isView ? "View" : "Add"} Vendor Details</strong>
          </h3>
          <button onClick={() => (result && !isView ? handleModalopen() : toggleFormFillrter())} class="btn btn-lg btn-primary px-md-5 px-4 addlistuser">
            Back
          </button>
        </div>

        <div class="user-details">
          <form onSubmit={handleSubmit(onSubmit)}>
            <div class="card">
              <div class="row card-body justify-content-around py-md-5 py-4">
                <div class="col-lg-5 col-md-6">
                  <div class="form-group mb-3">
                    <label for="code" class="form-label">
                      Vendor name<span class="text-danger">*</span>
                    </label>
                    <input {...register("vendor_name")} type="text" class="form-control py-2" id="code" disabled={isView} placeholder="Enter Vendor Name" />
                    <p className="text-danger">{errors.vendor_name?.message}</p>
                  </div>
                </div>

                <div class="col-lg-5 col-md-6">
                  <div class="form-group mt-md-3 mb-3">
                    <div class="form-check">
                      <input className="form-check-input mt-1" type="checkbox" id="flexCheckChecked" {...register("is_active")} disabled={isView} />
                      <label class="form-check-label mb-0" for="flexCheckChecked">
                        IS ACTIVE
                      </label>
                      <p className="text-danger">{errors.is_active?.message}</p>
                    </div>
                  </div>
                </div>

                <div class="col-lg-5 col-md-6">
                  <div class="form-group mb-3">
                    <label for="code" class="form-label">
                      Email<span class="text-danger">*</span>
                    </label>
                    <input {...register("email")} type="email" class="form-control py-2" id="code" disabled={isView} placeholder="Enter Email" />
                    <p className="text-danger">{errors.email?.message}</p>
                  </div>
                </div>

                <div class="col-lg-5 col-md-6">
                  <div class="form-group mb-3">
                    <label for="code" class="form-label">
                      Mobile No.<span class="text-danger">*</span>
                    </label>
                    <input {...register("mobile_no")} type="text" class="form-control py-2" id="code" disabled={isView} placeholder="Enter Mobile No." maxLength={10} />
                    <p className="text-danger">{errors.mobile_no?.message}</p>
                  </div>
                </div>
                <div class="col-lg-5 col-md-6">
                  <div class="form-group mb-3">
                    <label for="code" class="form-label">
                      GST No.<span class="text-danger">*</span>
                    </label>
                    <input {...register("gst_no")} type="text" class="form-control py-2" id="code" disabled={isView} placeholder="Enter GST No." />
                    <p className="text-danger">{errors.gst_no?.message}</p>
                  </div>
                </div>
                <div class="col-lg-5 col-md-6">
                  <div class="form-group mb-3">
                    <label for="code" class="form-label">
                      TAN No.<span class="text-danger">*</span>
                    </label>
                    <input {...register("tan_no")} type="text" class="form-control py-2" id="code" disabled={isView} placeholder="Enter TAN No." />
                    <p className="text-danger">{errors.tan_no?.message}</p>
                  </div>
                </div>
                <div class="col-lg-5 col-md-6">
                  <div class="form-group mb-3">
                    <label for="code" class="form-label">
                      PAN No.<span class="text-danger">*</span>
                    </label>
                    <input {...register("pan_no")} type="text" class="form-control py-2" id="code" disabled={isView} placeholder="Enter PAN No." />
                    <p className="text-danger">{errors.pan_no?.message}</p>
                  </div>
                </div>
                <div class="col-lg-5 col-md-6">
                  <div class="form-group mb-3">
                    <label for="code" class="form-label">
                      EPFO No.<span class="text-danger">*</span>
                    </label>
                    <input {...register("epfo_no")} type="text" class="form-control py-2" id="code" disabled={isView} placeholder="Enter EPFO No." maxLength={22} />
                    <p className="text-danger">{errors.epfo_no?.message}</p>
                  </div>
                </div>

                <div class="col-lg-5 col-md-6">
                  <div class="form-group mb-3">
                    <label for="code" class="form-label">
                      SECURITY CERTIFICATE NO.<span class="text-danger">*</span>
                    </label>
                    <input {...register("security_certificate_no")} type="text" class="form-control py-2" id="code" disabled={isView} placeholder="Enter Security Certificate No." />
                    <p className="text-danger">{errors.security_certificate_no?.message}</p>
                  </div>
                </div>

                <div class="col-lg-5 col-md-6">
                  <div class="form-group mb-3">
                    <label for="agreement_file" class="form-label">
                      UPLOAD AGREEMENT<span class="text-danger">*</span>
                    </label>
                    {!isView && <input {...register("agreement_file")} type="file" disabled={isView} className="form-control py-2" id="agreement_file" />}

                    {(isView || isEdit) && previousData?.agreement_file_url && (
                      <div className="pt-2">
                        <a href={previousData.agreement_file_url} target="_blank" rel="noopener noreferrer">
                          View Uploaded Agreement
                        </a>
                      </div>
                    )}

                    <p className="text-danger">{errors?.agreement_file?.message}</p>
                  </div>
                </div>

                <div class="col-12 text-center mt-lg-5 mt-4 d-sm-inline-flex justify-content-sm-center d-grid gap-3">
                  <button disabled={isView || isSubmitting} className="btn btn-lg btn-primary px-5" type="submit">
                    {isEdit ? "UPDATE" : isSubmitting ? "SUBMITTING..." : "SUBMIT"}
                  </button>
                  <button disabled={isView || isEdit} class="btn btn-lg btn-primary px-5 mx-xl-4" type="button" onClick={() => reset()}>
                    CLEAR
                  </button>
                </div>
              </div>
            </div>
          </form>
        </div>
        <BackButtonModal isOpen={modal?.isOpen} handleClose={handleClose} toggleFormFillrter={toggleFormFillrter} />
      </section>
    </>
  );
};

export default VendorForm;
